import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { IMyPerformanceDashboardData } from './MyPerformanceDashboard.types';
import { buildDashboardFromApiResponses, getEmptyDashboardData } from './myPerformanceTransformer';

export const WIDGET_KEYS = [
  'TOA',
  'GM',
  'Retention',
  'Cancellations',
  'ProgressBars',
  'CriticalTasks',
  'CandidateCounts',
  'PipelineAlerts',
  'Pipeline',
] as const;

export type WidgetKey = (typeof WIDGET_KEYS)[number];

export interface IRecruiterOption {
  id: number;
  name: string;
  value: number;
  object: { userId: number; firstName?: string; lastName?: string };
}

export interface IMyPerformanceState {
  data: IMyPerformanceDashboardData;
  responses: Partial<Record<WidgetKey, any>>;
  widgetLoaded: Partial<Record<WidgetKey, boolean>>;
  loadError: boolean;
  recruiterOptions: IRecruiterOption[];
  selectedEmployeeId: number | null;
}

export const myPerformanceSliceKey = 'myPerformance';

const initialState: IMyPerformanceState = {
  data: getEmptyDashboardData(),
  responses: {},
  widgetLoaded: {},
  loadError: false,
  recruiterOptions: [],
  selectedEmployeeId: null,
};

const slice = createSlice({
  name: myPerformanceSliceKey,
  initialState,
  reducers: {
    setWidgetData(state, action: PayloadAction<{ widget: WidgetKey; response: any }>) {
      const { widget, response } = action.payload;
      state.responses[widget] = response;
      state.widgetLoaded[widget] = true;
      state.data = buildDashboardFromApiResponses(state.responses as Record<string, any>);
    },
    setWidgetLoadError(state, action: PayloadAction<WidgetKey>) {
      state.widgetLoaded[action.payload] = true;
    },
    resetFetch(state) {
      state.responses = {};
      state.widgetLoaded = {};
      state.data = getEmptyDashboardData();
      state.loadError = false;
    },
    setLoadError(state, action: PayloadAction<boolean>) {
      state.loadError = action.payload;
    },
    setRecruiterOptions(state, action: PayloadAction<IRecruiterOption[]>) {
      state.recruiterOptions = action.payload;
    },
    setSelectedEmployeeId(state, action: PayloadAction<number | null>) {
      state.selectedEmployeeId = action.payload;
    },
  },
});

export const myPerformanceReducer = slice.reducer;

export const fetchMyPerformanceDashboardAction = createAction<{
  employeeId: number;
  startDate?: string;
  endDate?: string;
  daysBack?: number;
}>('FETCH_MY_PERFORMANCE_DASHBOARD');

export const fetchRecruiterOptionsAction = createAction<{ userId: number }>('FETCH_MY_PERFORMANCE_RECRUITER_OPTIONS');

export const myPerformanceActions = {
  ...slice.actions,
  fetchMyPerformanceDashboardAction,
  fetchRecruiterOptionsAction,
};
